import { useEffect, useMemo } from 'react';

import Container from 'lib/UserProfile/components/Container';
import { getBookmarks, getCreatorProducts, getPurchasedProducts } from '@tsClient';
import { getUrl, IDENTIFIERS } from 'helpers/urlsHelper';
import { CardType } from 'lib/UserProfile/types';
import useNoodleApi from '@hooks/useNoodleApi';
import * as Types from '@typings/graphql-models';
import { formatDistance } from 'date-fns';
import { useUser } from '@hooks';
import { m } from 'framer-motion';
import ProgressIndicator from '@components/ProgressIndicator';
import { DEFAULT_CURRENCY } from '@typings/common';

type ThisProduct = Pick<Types.Product, 'createdAt' | 'slug' | 'title' | 'id'> & {
  image?: { url: string } | null;
  tags?: Array<{ name: string }>;
  handbookSteps: Array<{ id: string }>;
};

const YourBookmarks: React.FC<{ creatorSlug: string }> = ({ creatorSlug }) => {
  const [user] = useUser();

  const {
    data: products,
    fetchingState: { isFetching: productsFetching },
    getData: getCreatorProductsFn,
  } = useNoodleApi(getCreatorProducts);
  const {
    data: bookmarks,
    fetchingState: { isFetching: bookmarksFetching },
    getData: getBookmarksFn,
  } = useNoodleApi(getBookmarks);
  const {
    data: purchasedProducts,
    fetchingState: { isFetching: purchasedProductsFetching },
    getData: getProducts,
  } = useNoodleApi(getPurchasedProducts);

  useEffect(() => {
    if (user) {
      getBookmarksFn();
    }
  }, [user, getBookmarksFn]);

  useEffect(() => {
    getCreatorProductsFn({ creatorSlug });
    getProducts({ creatorSlug });
  }, [getCreatorProductsFn, creatorSlug, getProducts]);

  const dataToDisplay = useMemo(() => {
    const list: ThisProduct[] = [];
    Object.keys(bookmarks || {}).forEach(key => {
      const product = products?.products?.find(p => p.id === key);
      if (product) {
        list.push(product);
      }
    });
    return list;
  }, [bookmarks, products]);

  if (productsFetching || bookmarksFetching || purchasedProductsFetching) {
    return <ProgressIndicator isCentered />;
  }

  if (!dataToDisplay.length) {
    return (
      <m.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="body-sm">
        You currently have no bookmarked items.
      </m.p>
    );
  }

  return (
    <Container
      title={`Your bookmarks`}
      subTitle={`${dataToDisplay.length} items`}
      chunk={3}
      isList
      data={dataToDisplay.map(p => ({
        image: p?.image?.url || '',
        inContainer: true,
        isInUserProfile: true,
        isNew: formatDistance(new Date(p.createdAt), new Date(), { addSuffix: true }) === 'less than 1 week ago',
        isPurchased: purchasedProducts?.find(({ id }) => id === p.id) !== undefined,
        link: getUrl(IDENTIFIERS.PRODUCT, { creatorSlug, productSlug: p.slug }),
        price: purchasedProducts?.find(({ id }) => id === p.id)?.prices?.[0] || { currency: DEFAULT_CURRENCY, price: 0 },
        steps: p.handbookSteps.length,
        tags: p.tags?.map(({ name }: { name: string }) => name),
        title: p?.title || '',
        type: CardType.Plan,
      }))}
    />
  );
};

export default YourBookmarks;
